/**
 * Version constants for code libraries:
 * - DEFAULT_INITIATED_VERSION: Used during initial creation of profile and resources at /init
 * - FIRST_INTERNAL_VERSION: The first version assigned when a library is actually shared at /share
 */
export const DEFAULT_INITIATED_VERSION = '0.0.0-rc';
export const FIRST_INTERNAL_VERSION = '1.0.0';

export const ComponentOptions = {
  ApplyTagSelectors: 'applyTagSelectors',
  Ssr: 'ssr',
  Prerender: 'prerender',
} as const;

export const PROJECTED_PACKAGE_JSON_FILENAME = '/package.json';
export const PROJECTED_INDEX_FILENAME = '/index.ts';
export const PROJECTED_THEME_FILENAME = '/theme.css';
export const PROJECTED_GLOBALS_FILENAME = '/globals.js';

/**
 * Import line prepended to every projected component file so the browser-API
 * shims load before the component (see `addBrowserApiShimsToComponentFiles`).
 */
export const PROJECTED_GLOBALS_IMPORT = `import '.${PROJECTED_GLOBALS_FILENAME}';`;

/**
 * Lines to subtract from a build-time `data-wf-loc-text` line number to land on
 * the stored `source.code` — i.e. how many lines the stamping build's file has
 * ahead of the component's own source.
 *
 * Zero, because the dev-preview sandbox projection (`projectComponentsToFiles`)
 * writes `source.code` verbatim: built-file lines and stored-source lines are
 * the same numbers. This was previously derived from
 * `PROJECTED_GLOBALS_IMPORT`, which describes the PUBLISHED bundle's layout
 * (`addBrowserApiShimsToComponentFiles`) and not the file the stamping build
 * transforms, so every stamp came back one line early (EXT-1735).
 *
 * Named rather than inlined because the coupling is real and invisible: if the
 * sandbox projection ever gains a prefix, this is the one place to follow it,
 * and the cross-boundary test in
 * `packages/domains/code-libraries/templates/__tests__/sandbox/base/babel-plugin-source-loc.test.ts`
 * pins it against the actual projection so it cannot silently go stale again.
 */
export const STAMPED_BUILD_LINE_OFFSET = 0;
export const WEBFLOW_CONFIG_FILENAME = '/webflow.json';
export const SANDBOX_WORKING_DIR = '/webflow';

/**
 * Blaxel web console for a sandbox workload id (Code Components / internal debug).
 * Used from Admin for support links.
 */
const BL_WORKSPACE = process.env.BL_WORKSPACE || 'webflow-test';
export const BLAXEL_CODE_COMPONENTS_SANDBOX_CONSOLE_URL_BASE = `https://app.blaxel.ai/${BL_WORKSPACE}/global-agentic-network/sandbox`;

export function getBlaxelCodeComponentsSandboxConsoleUrl(
  sandboxId: string
): string {
  return `${BLAXEL_CODE_COMPONENTS_SANDBOX_CONSOLE_URL_BASE}/${encodeURIComponent(sandboxId)}`;
}

export const SANDBOX_IGNORED_EXTENSIONS = ['.tmp', '.lock', '.lockb'];
export const SKILLS_DIRECTORY = '.skills';

export const SANDBOX_WATCH_IGNORE_DIRECTORIES = [
  'node_modules',
  'dist',
  'out',
  '.vite',
  '.skills',
  // Multi-MB function bundles; walking them can mint them as source files.
  'dist-functions',
  'dist-functions-versions',
];

export const WEBFLOW_DEPENDENCIES = [
  '@webflow/data-types',
  '@webflow/functions',
  '@webflow/react',
  '@webflow/webflow-cli',
];
export const DEFAULT_DEPENDENCIES = [
  '@babel/core',
  '@rolldown/plugin-babel',
  '@tailwindcss/postcss',
  '@tailwindcss/vite',
  '@types/react',
  '@types/react-dom',
  '@vitejs/plugin-react',
  'react',
  'react-dom',
  'tailwindcss',
  'tailwind-merge',
  'typescript',
  'vite',
];

export const SANDBOX_MANAGED_DEPENDENCIES = [
  '@babel/core',
  '@rolldown/plugin-babel',
  '@vitejs/plugin-react',
  '@tailwindcss/vite',
  '@tailwindcss/postcss',
  '@types/react',
  '@types/react-dom',
  'react',
  'react-dom',
  'tailwindcss',
  'typescript',
  'vite',
];

export const INITIAL_CODE_COMPONENT_SOURCE = `import { declareComponent } from "@webflow/react";

function Component() {
  return <div></div>;
}

export default declareComponent(Component, {
  name: "Code Component",
  description: "A simple getting started component.",
  group: "Basics",
  props: {},
});
`;
